export type DurationUnit = 's' | 'min' | 'h';

export const DURATION_UNITS: DurationUnit[] = ['s', 'min', 'h'];

const SECONDS_PER_UNIT: Record<DurationUnit, number> = { s: 1, min: 60, h: 3600 };

const DURATION_VALUE_PATTERN = /^(-?\d+(?:[.,]\d+)?)\s*(s|min|h)$/i;

function isDurationUnit(value: string): value is DurationUnit {
  return DURATION_UNITS.includes(value.toLowerCase() as DurationUnit);
}

export function secondsToAmount(seconds: number, unit: DurationUnit): number {
  return seconds / SECONDS_PER_UNIT[unit];
}

export function amountToSeconds(amount: number, unit: DurationUnit): number {
  return Math.round(amount * SECONDS_PER_UNIT[unit]);
}

export function formatDuration(seconds: number, unit: DurationUnit, language: string): string {
  const amount = secondsToAmount(seconds, unit);
  const formattedAmount = new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(
    amount
  );
  return formattedAmount + ' ' + unit;
}

export function serializeDeviceSettingDuration(amount: number, unit: DurationUnit): string {
  if (Number.isNaN(amount)) {
    return unit;
  }
  return String(amount) + ' ' + unit;
}

export function isRecordableDeviceSettingValue(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && isDurationUnit(trimmed) === false;
}

export function parseDeviceSettingDurationValue(
  value: string,
  fallbackUnit: DurationUnit
): { amount: number; unit: DurationUnit } {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { amount: Number.NaN, unit: fallbackUnit };
  }
  if (isDurationUnit(trimmed)) {
    return { amount: Number.NaN, unit: trimmed };
  }
  const match = DURATION_VALUE_PATTERN.exec(trimmed);
  if (match !== null) {
    const matchedAmount = match[1];
    const matchedUnit = match[2];
    if (matchedAmount !== undefined && matchedUnit !== undefined && isDurationUnit(matchedUnit)) {
      const amount = Number(matchedAmount.replace(',', '.'));
      if (!Number.isNaN(amount)) {
        return { amount, unit: matchedUnit.toLowerCase() as DurationUnit };
      }
    }
  }
  const bareNumber = Number(trimmed.replace(',', '.'));
  if (trimmed.length > 0 && !Number.isNaN(bareNumber)) {
    return { amount: secondsToAmount(bareNumber, fallbackUnit), unit: fallbackUnit };
  }
  return { amount: Number.NaN, unit: fallbackUnit };
}

export function toEditableDeviceSettingValue(
  storedValue: string,
  storedUnit: string | null,
  fieldType: string | null
): string {
  if (fieldType !== 'duration' || storedUnit === null || isDurationUnit(storedUnit) === false) {
    return storedValue;
  }
  const seconds = Number(storedValue);
  if (storedValue.trim().length === 0 || Number.isNaN(seconds)) {
    return storedValue;
  }
  return serializeDeviceSettingDuration(secondsToAmount(seconds, storedUnit), storedUnit);
}
